import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import successSound from "../assets/sounds/valider.wav";
import failSound from "../assets/sounds/erreur.mp3";
import Breadcrumbs from '../components/Breadcrumbs';

const correctSound = new Audio(successSound);
const errorSound = new Audio(failSound);

const ExerciceVocabulaireClassique = () => {
    const API_URL = process.env.REACT_APP_API_URL;
    const { interfaceLang, courseLang, vocabularyCategory, exerciseType } = useParams(); 
    const navigate = useNavigate();
    const [sessionId, setSessionId] = useState(null);
    const [exercise, setExercise] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Contrôle du son
    const [isMuted, setIsMuted] = useState(false);

    // États spécifiques au QCM
    const [qcmOptions, setQcmOptions] = useState([]); // tableau de 4 propositions

    // status: "idle" | "OK" | "NOK"
    const [qcmResult, setQcmResult] = useState({ status: "idle", selected: null });

    // États spécifiques à la traduction avec suggestion
    const [userTranslation, setUserTranslation] = useState("");
    const [suggestions, setSuggestions] = useState([]);

    // États spécifiques à traduction-letter
    const [availableLetters, setAvailableLetters] = useState([]);
    const [selectedLetters, setSelectedLetters] = useState([]);
    const [answerSlots, setAnswerSlots] = useState([]); // tableau de la longueur de la traduction attendue, initialisé avec null
    const [selectedSlot, setSelectedSlot] = useState(null); // index de la case actuellement sélectionnée
    
    // Récupérer ou créer la session active pour cet exercice (exemple)
    const fetchOrCreateSession = async () => {
        const token = localStorage.getItem('access_token');
        try {
        // On suppose ici un endpoint qui renvoie une session active ou en crée une
        const response = await axios.get(
            `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/session/${exerciseType}/`,
            { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSessionId(response.data.id);
        } catch (error) {
        console.error("Erreur lors de la récupération de la session", error);
        }
    };

    // Récupérer un exercice (mot) pour la session
    const fetchExercise = () => {
        if (!sessionId) return;
        const token = localStorage.getItem('access_token');
        axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/?sessionId=${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
        // response.data doit contenir { word: "apple", translation: "manzana", ... }
        console.log(response)
        if (response.status === 204 || response.data == null || response.data == "") {
            navigate(`/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}`);
            return;
        }

        setExercise(response.data);
        //setFeedback("");
        // Pour chaque type d'exercice, initialiser l'UI
        if (exerciseType === "qcm") {
            fetchQcmOptions(response.data);
        } else if (exerciseType === "traduction") {
            setUserTranslation("");
            fetchTranslationSuggestions(""); // initialisation (liste vide ou complet)
        } else if (exerciseType === "traduction-letter") {
            generateLetterExercise(response.data);
        }
        })
        .catch(error => {
        console.error("Erreur lors de la récupération du mot", error);
        });
    };

    // Pour QCM : récupérer 3 mauvaises traductions et mélanger avec la bonne
    const fetchQcmOptions = (exerciseData) => {
        const token = localStorage.getItem('access_token');
        axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises-qcm/?sessionId=${sessionId}&translation=${exerciseData.translation}`, {
        headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
            // response.data doit être un tableau de 3 mauvaises traductions, ex: ["plátano", "uva", "naranja"]
            console.log(response)
            const wrongOptions = response.data;
            const goodOptions = { [exerciseData.word]: exerciseData.translation };
            const allOptions = [...wrongOptions, goodOptions];
            // Mélanger les options
            const shuffledOptions = allOptions.sort(() => Math.random() - 0.5);
            setQcmOptions(shuffledOptions);
        })
        .catch(error => {
        console.error("Impossible de récupérer les traductions pour le QCM", error);
        });
    };


    // Pour traduction : récupérer des suggestions en fonction du préfixe
    const fetchTranslationSuggestions = (prefix) => {
        const token = localStorage.getItem('access_token');
        axios.get(`${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises-traduction-suggestions/?sessionId=${sessionId}&prefix=${prefix}`, {
        headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
            console.log("recup des suggestions")
            console.log(response)
            // response.data doit être un tableau de suggestions
            setSuggestions(response.data);
        })
        .catch(error => {
        console.error("Impossible de récupérer les suggestions", error);
        });
    };

    // 1) Supprime " ( ... )" et l'espace juste avant les /, mais garde les autres espaces
    const cleanWordForExercice = (raw) => {
    if (!raw) return '';
    let cleaned = raw;

    // 1) enlever espace + parenthèse
    // ex: "ORANGE (COLOUR)" -> "ORANGE" | "UNITED STATE OF AMERICA (USA)" -> "UNITED STATE OF AMERICA"
    cleaned = cleaned.replace(/\s*\([^)]*\)\s*/g, '').trim();

    // 2) si "/" présent → garder seulement ce qui est avant
    // ex: "colors / colours" → "colors"
    if (cleaned.includes('/')) {
        cleaned = cleaned.split('/')[0].trim();
    }

    return cleaned;

    };


    // Pour traduction-letter : préparer les lettres à afficher
    const generateLetterExercise = (exerciseData) => {

        const cleanedDisplay = cleanWordForExercice(exerciseData.word);
        const cleanedDisplayUpper = cleanedDisplay.toUpperCase();
        //const correct = exerciseData.word.toUpperCase();
        let letters = cleanedDisplayUpper.split('');
        
        // Ajouter quelques lettres aléatoires (par exemple 3 lettres en plus)
        const extraCount = 3;
        for (let i = 0; i < extraCount; i++) {
            letters.push(String.fromCharCode(65 + Math.floor(Math.random() * 26)));
        }
        // Mélanger les lettres
        letters = letters.sort(() => Math.random() - 0.5);
        setAvailableLetters(letters);
        setSelectedLetters([]);


        // Créer un tableau de cases de la taille du mot attendu (chaque case initialisée à null)
        setAnswerSlots(new Array(cleanedDisplayUpper.length).fill(null));
        setSelectedSlot(null);
    };

    // Fonction appelée lors du clic sur un bouton de lettre (disponible)
    const handleLetterSelect = (letter, letterIndex) => {
        // Si une case est sélectionnée, on y place la lettre
        let slotIndex = selectedSlot;
        if (slotIndex === null) {
        // Si aucune case n'est sélectionnée, on remplit la première case vide
        slotIndex = answerSlots.findIndex(slot => slot === null);
        if (slotIndex === -1) return; // aucune case vide
        }
        const newSlots = [...answerSlots];
        newSlots[slotIndex] = { letter, sourceIndex: letterIndex };
        setAnswerSlots(newSlots);
        setSelectedSlot(null);
    };
    

    // Fonction appelée lors du clic sur une case de réponse
    const handleSlotClick = (slotIndex) => {
        // Si la case est déjà sélectionnée, on la désélectionne
        if (selectedSlot === slotIndex) {
        setSelectedSlot(null);
        } else {
        // Si la case est remplie, la retirer (ce qui rend la lettre disponible à nouveau)
        const newSlots = [...answerSlots];
        if (newSlots[slotIndex]) {
            newSlots[slotIndex] = null;
            setAnswerSlots(newSlots);
        } else {
            // Sinon, sélectionner la case pour y placer une lettre
            setSelectedSlot(slotIndex);
        }
        }
    };

    // Lors du montage, récupérer la session, puis l'exercice
    useEffect(() => {
        fetchOrCreateSession();
    }, [vocabularyCategory, exerciseType]);


    useEffect(() => {
        if (sessionId) {
          fetchExercise();
        }
    }, [sessionId, vocabularyCategory, exerciseType]);
    
    // Fonction générique pour gérer la validation d'un mot
    const handleValidation = (isCorrect, wordKey, translation) => {
        if (submitting) return; // ignore double tap
        setSubmitting(true);
        
        const token = localStorage.getItem('access_token');
        const endpoint = isCorrect 
            ? `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/validate/`
            : `${API_URL}/cours/api/vocabulaire/${vocabularyCategory}/exercises/error/`;
        
        // Jouer le son
        if (!isMuted) {
            isCorrect ? correctSound.play() : errorSound.play();
        }
        
        // Gestion du feedback
        if (isCorrect) {
            setFeedback('');
        }
        else {
            setFeedback(`Erreur : ${wordKey} => ${translation}`);
        }

        axios.post(endpoint, {
            sessionId: sessionId,
            word: wordKey
        }, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(() => {
            // Après validation, on récupère un nouveau mot
            fetchExercise();
        })
        .catch(error => {
            console.error("Erreur lors de la validation", error);
            fetchExercise();
        })
        .finally(() => {
            setSubmitting(false);
        });

    };

    // Fonction spécifique QCM
    const handleValidationQcm = (isCorrect, wordKey, translation, index) => {

        // Modif bouton
        setQcmResult({ status: isCorrect ? "OK" : "NOK", selected: index });

        // laisser le temps d’afficher la couleur, puis reset + charger le mot suivant
        setTimeout(() => {
            setQcmResult({ status: "idle", selected: null });
        }, 300);
        handleValidation(isCorrect, wordKey, translation)
        
        

    }


    // Rendu pour le QCM
    const renderQcm = () => {
        return (
        <div>
            <p className="text-xl mb-4">Traduisez : <span className="font-bold">{exercise.word}</span></p>
            <div className="grid grid-cols-2 gap-4">
            {qcmOptions.map((option, index) => {
                const word = Object.keys(option)[0];
                const translation = Object.values(option)[0];
                const isSelected = qcmResult.selected === index;
                return (
                    <button
                    key={index}
                    className={`px-4 py-8  
                    ${
                        isSelected && qcmResult.status === 'OK' 
                        ? 'bg-green-200 text-green-500'
                        : isSelected && qcmResult.status === 'NOK' 
                        ? 'bg-red-200 text-red-500'  
                        : 'bg-white can-hover'
                    }
                    
                    
                    border border-gray-300 rounded transition`}
                    onClick={() => handleValidationQcm(translation === exercise.translation, exercise.word, exercise.translation, index)}
                    disabled={submitting}
                    >
                    {translation}
                    </button>
                );
            })}
            </div>
            {/* Insertion du style pour l'animation shake */}
            <style jsx>{`
                @media (hover: hover) and (pointer: fine) {
                    .can-hover:hover {
                        box-shadow: var(--tw-shadow);
                        --tw-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1);
                        background-color: #eff6ff; /* bleu-50 */
                    }
                }
            `}</style>
        </div>
        );
    };

    // Rendu pour la traduction (type input avec suggestions)
    const renderTraduction = () => {
        return (
        <div>
            <p className="text-xl mb-4">Traduisez : <span className="font-bold">{exercise.word}</span></p>
            <input 
            type="text" 
            value={userTranslation}
            onChange={(e) => {
                setUserTranslation(e.target.value);
                fetchTranslationSuggestions(e.target.value);
            }}
            className="px-4 py-2 border border-gray-300 rounded w-full mb-2"
            placeholder="Tapez votre traduction..."
            />
            {suggestions && suggestions.length > 0 && (
            <ul className="border border-gray-200 rounded p-2 mb-2">
                {suggestions.map((sugg, idx) => (
                <li 
                    key={idx} 
                    className="cursor-pointer hover:bg-blue-50 p-1"
                    onClick={() => setUserTranslation(Object.values(sugg)[0])}
                >
                    {Object.values(sugg)[0]}
                </li>
                ))}
            </ul>
            )}
            <button 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
            onClick={() => {
                // Comparer la traduction saisie à la réponse attendue (insensible à la casse)
                const isCorrect = userTranslation.trim().toLowerCase() === exercise.translation.trim().toLowerCase();
                handleValidation(isCorrect, exercise.word, exercise.translation);
            }}
            disabled={submitting}
            >
            Valider
            </button>
        </div>
        );
    };

    // Rendu pour la traduction-letter
    const renderTraductionLetter = () => {
        return (
        <div>
            <p className="text-xl mb-4">Reconstituez la traduction de : <span className="font-bold">{exercise.translation}</span></p>
            {/* Affichage des cases correspondant au mot à reconstituer */}
            <div className="mb-4 flex space-x-2">
                {answerSlots.map((slot, index) => (
                <div 
                    key={index}
                    className={`w-12 h-12 border border-gray-300 rounded flex items-center justify-center cursor-pointer transition ${
                    selectedSlot === index ? 'bg-blue-100' : 'bg-white'
                    }`}
                    onClick={() => handleSlotClick(index)}
                >
                    {slot ? slot.letter : ""}
                </div>
                ))}
            </div>
            <button 
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition mb-4"
                onClick={() => {
                const answer = answerSlots.map(slot => slot ? slot.letter : "").join('');
                const target = cleanWordForExercice(exercise.word);
                const isCorrect = answer.toLowerCase() === target.trim().toLowerCase();
                handleValidation(isCorrect, exercise.word, exercise.translation);
                }}
                disabled={submitting}
            >
                Valider
            </button>
            {/* Affichage des lettres disponibles */}
            <div className="flex flex-wrap gap-2">
                {availableLetters.map((letter, index) => {
                // Désactiver le bouton si cette lettre (à cet index) est déjà utilisée dans une case
                const isUsed = answerSlots.some(slot => slot && slot.sourceIndex === index);
                return (
                    <button 
                    key={index}
                    className={`w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded hover:bg-blue-50 transition ${
                        isUsed ? 'invisible' : ''
                        }`}
                    onClick={() => handleLetterSelect(letter, index)}
                    disabled={isUsed}
                    >
                    {letter}
                    </button>
                );
                })}
            </div>
        </div>
        );
    };
    
    // Choix de l'affichage en fonction du type d'exercice
    const renderForm = () => {
        switch (exerciseType) {
        case "qcm":
            return renderQcm();
        case "traduction":
            return renderTraduction();
        case "traduction-letter":
            return renderTraductionLetter();
        default:
            return <p className="text-red-500">Type d'exercice inconnu</p>;
        }
    };

    // fil d'Ariane
    const breadcrumbsLinks = [
    { label: 'Accueil', path: `/${interfaceLang}` },
    { label: 'Cours de langue', path:  `/${interfaceLang}/courses/${courseLang}` },
    { label: 'Vocabulaire', path: `/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}` },
    { label: 'Exercice', path: `/${interfaceLang}/courses/${courseLang}/vocabulary/${vocabularyCategory}/exercise/${exerciseType}` }
    ];

    return (
        <div className="max-w-4xl mx-auto mt-8 p-6 bg-white rounded-lg shadow-lg relative">
        {/* Bouton pour activer/désactiver le son */}
        <button
            className="absolute top-4 right-4 p-2 rounded-full border border-gray-300 hover:bg-gray-100 transition"
            onClick={() => setIsMuted(prev => !prev)}
        >
            {isMuted ? "Activer le son" : "Couper le son"}
        </button>
        
        {/* Fil d'Ariane */}
        <Breadcrumbs links={breadcrumbsLinks} />

        {exercise ? (
            <div>
            {/*<h2 className="text-2xl font-bold text-blue-600 mb-4">{exercise.word}</h2>*/}
            {renderForm()}
            {feedback && <p className="mt-4 text-lg text-center">{feedback}</p>}
            </div>
        ) : (
            <p className="text-gray-700">Chargement de l'exercice...</p>
        )}
        </div>
    );
    };
    
    export default ExerciceVocabulaireClassique;